import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  Box,
  Chip,
  LinearProgress,
  Stack,
  useTheme,
  alpha,
  Fade,
  Collapse,
} from '@mui/material';
import {
  Psychology as PsychologyIcon,
  AutoAwesome as AIIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  PlaylistAddCheck as NextActionsIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
} from '@mui/icons-material';
import { OpportunityAnalysisDTO } from '@inversor-hub/types';

interface AIAnalysisCardProps {
  onAnalyze: () => Promise<void>;
  analysis?: OpportunityAnalysisDTO;
  isAnalyzing: boolean;
}

const AIAnalysisCard: React.FC<AIAnalysisCardProps> = ({ onAnalyze, analysis, isAnalyzing }) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);

  const handleAnalyze = async () => {
    setIsExpanded(true);
    await onAnalyze();
  };

  const aiGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.accent.main} 100%)`;
  const aiGradientSubtle = `linear-gradient(135deg, ${alpha(
    theme.palette.primary.main,
    0.1
  )} 0%, ${alpha(theme.palette.accent.main, 0.1)} 100%)`;

  // Get fit label color and icon based on analysis
  const getFitDisplay = (analysis: OpportunityAnalysisDTO) => {
    const { fit, fitLabel } = analysis;

    if (fit) {
      switch (fitLabel) {
        case 'Strong':
          return {
            color: theme.palette.success.main,
            icon: CheckCircleIcon,
            label: 'Fuerte Compatibilidad',
            bgColor: alpha(theme.palette.success.main, 0.1),
          };
        case 'Moderate':
          return {
            color: theme.palette.warning.main,
            icon: InfoIcon,
            label: 'Compatibilidad Moderada',
            bgColor: alpha(theme.palette.warning.main, 0.1),
          };
        case 'Low':
          return {
            color: theme.palette.error.main,
            icon: WarningIcon,
            label: 'Baja Compatibilidad',
            bgColor: alpha(theme.palette.error.main, 0.1),
          };
      }
    }

    return {
      color: theme.palette.error.main,
      icon: ErrorIcon,
      label: 'No Compatible',
      bgColor: alpha(theme.palette.error.main, 0.1),
    };
  };

  const fitDisplay = analysis ? getFitDisplay(analysis) : null;

  return (
    <Card
      sx={{
        background: aiGradientSubtle,
        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <CardContent sx={{ pt: 3 }}>
        <Stack direction="row" alignItems="center" spacing={2} mb={2}>
          <Box
            sx={{
              p: 1.5,
              borderRadius: 2,
              background: aiGradient,
              color: 'white',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <PsychologyIcon />
          </Box>
          <Box flex={1}>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
              <Typography variant="h6" fontWeight="600">
                Análisis Inteligente
              </Typography>
              <Chip
                icon={<AIIcon sx={{ fontSize: '16px !important' }} />}
                label="IA"
                size="small"
                sx={{
                  background: aiGradient,
                  color: 'white',
                  fontWeight: 500,
                  '& .MuiChip-icon': { color: 'white' },
                }}
              />
              {analysis && (
                <Chip
                  label={`Score: ${Math.round(analysis.score)}%`}
                  size="small"
                  sx={{
                    fontWeight: 500,
                  }}
                />
              )}
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Descubre si esta oportunidad encaja con tus preferencias de inversión
            </Typography>
          </Box>
        </Stack>

        <Collapse in={isExpanded} timeout={300}>
          <Box>
            {isAnalyzing && (
              <Fade in>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body2" fontWeight="500">
                    Analizando oportunidad...
                  </Typography>
                  <LinearProgress
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: alpha(theme.palette.primary.main, 0.1),
                      '& .MuiLinearProgress-bar': {
                        background: aiGradient,
                        borderRadius: 3,
                      },
                    }}
                  />
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 1, display: 'block' }}
                  >
                    Comparando con tus preferencias y analizando métricas clave...
                  </Typography>
                </Box>
              </Fade>
            )}

            {analysis && fitDisplay && (
              <Fade in timeout={500}>
                <Box sx={{ mb: 3 }}>
                  {/* Fit Display */}
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 2,
                      backgroundColor: fitDisplay.bgColor,
                      border: `1px solid ${alpha(fitDisplay.color, 0.2)}`,
                      mb: 2,
                    }}
                  >
                    <Stack direction="row" alignItems="center" spacing={2}>
                      <fitDisplay.icon sx={{ color: fitDisplay.color, fontSize: 20 }} />
                      <Typography variant="body2" fontWeight="500" sx={{ lineHeight: 1.5 }}>
                        {fitDisplay.label}
                      </Typography>
                    </Stack>
                  </Box>

                  {/* Summary */}
                  {analysis.summary && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="600" mb={1} color="text.primary">
                        📋 Resumen
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2 }}>
                        {analysis.summary}
                      </Typography>
                    </Box>
                  )}

                  {/* Pros */}
                  {analysis.pros && analysis.pros.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="600" mb={1} color="text.primary">
                        👍 Pros
                      </Typography>
                      <Stack spacing={1}>
                        {analysis.pros.map((pro, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              backgroundColor: alpha(theme.palette.success.main, 0.08),
                              border: `1px solid ${alpha(theme.palette.success.main, 0.2)}`,
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                              <ThumbUpIcon
                                sx={{
                                  color: theme.palette.success.main,
                                  fontSize: 16,
                                  mt: 0.2,
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.5 }}>
                                {pro}
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Cons */}
                  {analysis.cons && analysis.cons.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="600" mb={1} color="text.primary">
                        👎 Contras
                      </Typography>
                      <Stack spacing={1}>
                        {analysis.cons.map((con, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              backgroundColor: alpha(theme.palette.error.main, 0.08),
                              border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                              <ThumbDownIcon
                                sx={{
                                  color: theme.palette.error.main,
                                  fontSize: 16,
                                  mt: 0.2,
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.5 }}>
                                {con}
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Risks */}
                  {analysis.risks && analysis.risks.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="600" mb={1} color="text.primary">
                        ⚠️ Riesgos
                      </Typography>
                      <Stack spacing={1}>
                        {analysis.risks.map((risk, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              backgroundColor: alpha(theme.palette.warning.main, 0.08),
                              border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}`,
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                              <WarningIcon
                                sx={{
                                  color: theme.palette.warning.main,
                                  fontSize: 16,
                                  mt: 0.2,
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.5 }}>
                                {risk}
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}

                  {/* Next Actions */}
                  {analysis.nextActions && analysis.nextActions.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" fontWeight="600" mb={1} color="text.primary">
                        🎯 Próximos Pasos
                      </Typography>
                      <Stack spacing={1}>
                        {analysis.nextActions.map((action, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 1.5,
                              borderRadius: 1,
                              backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                            }}
                          >
                            <Stack direction="row" spacing={1.5} alignItems="flex-start">
                              <NextActionsIcon
                                sx={{
                                  color: theme.palette.primary.main,
                                  fontSize: 16,
                                  mt: 0.2,
                                }}
                              />
                              <Typography variant="body2" sx={{ flex: 1, lineHeight: 1.5 }}>
                                {action}
                              </Typography>
                            </Stack>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Fade>
            )}
          </Box>
        </Collapse>
      </CardContent>

      <CardActions sx={{ px: 3, pb: 3 }}>
        {!isExpanded ? (
          <Button
            variant="contained"
            onClick={handleAnalyze}
            disabled={isAnalyzing}
            startIcon={<PsychologyIcon />}
            sx={{
              background: aiGradient,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.accent.dark} 100%)`,
              },
              borderRadius: 2,
              px: 3,
              py: 1,
              fontWeight: 600,
              textTransform: 'none',
            }}
            fullWidth
          >
            Analizar
          </Button>
        ) : (
          analysis && (
            <Button
              variant="outlined"
              onClick={() => setIsExpanded(false)}
              sx={{
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: theme.palette.primary.main,
                borderRadius: 2,
                textTransform: 'none',
              }}
              size="small"
            >
              Contraer análisis
            </Button>
          )
        )}
      </CardActions>
    </Card>
  );
};

export default AIAnalysisCard;
